
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, RefreshCw } from 'lucide-react';
import { wellnessService } from '@/services/wellnessService';
import { UserPersona, CheckInData } from '@/types/user';
import { useUser } from '@/contexts/UserContext';

type ReflectionPromptProps = {
  prompt?: string;
  onSubmit?: (response: string) => void;
  onPromptSelect?: (prompt: string) => void;
  selectedPrompt?: string;
  userPersona?: UserPersona;
  recentMood?: number;
  recentThemes?: string[];
  enableAI?: boolean;
};

const ReflectionPrompt = ({ 
  prompt, 
  onSubmit, 
  onPromptSelect, 
  selectedPrompt, 
  userPersona, 
  recentMood, 
  recentThemes, 
  enableAI = true 
}: ReflectionPromptProps) => {
  const [response, setResponse] = useState('');
  const [aiPrompts, setAiPrompts] = useState<string[]>([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [userHistory, setUserHistory] = useState<CheckInData[]>([]);
  const { user } = useUser();
  const handleSubmit = () => {
    if (response.trim() && onSubmit) {
      onSubmit(response);
      setResponse('');
    }
  };

  // Enhanced reflection prompts (fallback) - more diverse and engaging
  const defaultPrompts = [
    // Growth & Learning focused
    "What surprised you most about yourself today?",
    "If you could replay one conversation from today, what would you change?",
    "What's one small victory you had today that you want to remember?",
    
    // Energy & Patterns focused
    "When did you feel most energized today, and what was happening in that moment?",
    "What drained your energy today, and how could you handle it differently next time?",
    "If your energy levels were a song today, what would it sound like?",
    
    // Future & Action focused
    "What's one thing you're excited to tackle tomorrow?",
    "If tomorrow's you could send today's you a message, what would it be?",
    "What would make tomorrow feel like a 'win' for you?",
    
    // Gratitude & Perspective focused
    "Who made your day a little brighter, and how?",
    "What's something ordinary that felt special today?",
    "What are you appreciating about yourself right now?",
    
    // Creative & Metaphorical
    "If today was a weather pattern, what would it be and why?",
    "What emoji best captures your day, and what's the story behind it?",
    "If you were to describe today to a close friend in one sentence, what would you say?"
  ];

  // Fetch user history for personalized prompts
  const fetchUserHistory = async () => {
    if (!user?.id) return;
    
    try {
      const data = await wellnessService.getCheckIns({
        userId: user.id,
        limit: 14, // Last 2 weeks for pattern analysis
        orderBy: 'date',
        orderDirection: 'desc'
      });

      setUserHistory(data || []);
    } catch (error) {
      console.error('Error fetching user history:', error);
    }
  };

  // Generate AI-powered prompts with deep personalization
  // MIGRATED TO BACKEND: Now uses wellnessService.getReflectionPrompt() which returns string (1 prompt per call)
  // Backend endpoint: GET /wellness/reflection-prompt
  // MIGRATED FROM web_old/src/services/openaiService.ts::generateReflectionPrompt()
  // ALIGNED WITH web_old: Loop 3x to generate 3 AI prompts, then mix with 2 default prompts
  const generateAIPrompts = async () => {
    if (!enableAI || !user) return;

    setIsLoadingPrompts(true);
    try {
      const prompts: string[] = [];
      
      // ALIGNED WITH web_old: Generate 3 deeply personalized AI prompts using user history (loop 3x)
      for (let i = 0; i < 3; i++) {
        try {
          const aiPrompt = await wellnessService.getReflectionPrompt(recentMood, recentThemes);
          if (aiPrompt && typeof aiPrompt === 'string') {
            prompts.push(aiPrompt);
          }
        } catch (error) {
          console.error(`Error generating AI prompt ${i + 1}:`, error);
          // Continue to next iteration if one fails
        }
      }
      
      // ALIGNED WITH web_old: Mix in 2 carefully selected default prompts for variety
      const shuffledDefaults = [...defaultPrompts].sort(() => 0.5 - Math.random());
      prompts.push(...shuffledDefaults.slice(0, 2));
      
      // ALIGNED WITH web_old: Shuffle the final mix for better UX
      const finalPrompts = prompts.sort(() => 0.5 - Math.random());
      setAiPrompts(finalPrompts);
    } catch (error) {
      console.error('Error generating AI prompts:', error);
      // ALIGNED WITH web_old: Fallback to randomized default prompts
      const shuffledDefaults = [...defaultPrompts].sort(() => 0.5 - Math.random());
      setAiPrompts(shuffledDefaults.slice(0, 5));
    } finally {
      setIsLoadingPrompts(false);
    }
  };

  // Fetch user history when component mounts or user changes
  useEffect(() => {
    fetchUserHistory();
  }, [user?.id]);

  // Clear old prompts and generate new ones when context changes
  useEffect(() => {
    if (userHistory.length > 0 || !enableAI) {
      setAiPrompts([]); // Clear old prompts first
      generateAIPrompts();
    }
  }, [enableAI, user, userPersona, recentMood, recentThemes, userHistory]);

  const currentPrompts = aiPrompts.length > 0 ? aiPrompts : defaultPrompts;
  
  // If selectedPrompt and onPromptSelect are provided, show prompt selection
  if (onPromptSelect && selectedPrompt !== undefined) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium">
              {enableAI && aiPrompts.length > 0 ? 'Personalized Reflection Prompts' : 'Reflection Prompts'}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {enableAI && aiPrompts.length > 0 
                ? `Deeply personalized using ${userHistory.length > 0 ? `${userHistory.length} check-ins` : 'your current context'}` 
                : 'Choose a prompt that resonates with you today'}
            </p>
          </div>
          {enableAI && (
            <Button
              variant="outline"
              size="sm"
              onClick={generateAIPrompts}
              disabled={isLoadingPrompts}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingPrompts ? 'animate-spin' : ''}`} />
              {isLoadingPrompts ? 'Generating...' : 'New Prompts'}
            </Button>
          )}
        </div>
        
        {isLoadingPrompts ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-8 bg-muted animate-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {currentPrompts.map((promptText, index) => (
              <Button
                key={index}
                type="button"
                variant={selectedPrompt === promptText ? "default" : "outline"}
                size="sm"
                onClick={() => onPromptSelect(promptText)}
                className="text-xs"
              >
                {promptText.length > 30 ? promptText.substring(0, 27) + '...' : promptText}
              </Button>
            ))}
          </div>
        )}
        
        {selectedPrompt && (
          <div className="p-4 bg-muted/30 rounded-md border">
            <p className="text-sm font-medium">{selectedPrompt}</p>
          </div>
        )}
      </div>
    );
  }
  
  // Default view when prompt is provided
  return (
    <div className="bg-card rounded-xl border p-5 shadow-sm">
      <h3 className="text-lg font-medium mb-3">{prompt}</h3>
      <Textarea
        placeholder="Reflect on this prompt..."
        className="min-h-32 mb-3 resize-none"
        value={response}
        onChange={(e) => setResponse(e.target.value)}
      />
      <div className="flex justify-end">
        <Button onClick={handleSubmit} className="flex items-center gap-2">
          <span>Submit</span>
          <Send size={16} />
        </Button>
      </div>
    </div>
  );
};

export default ReflectionPrompt;
